import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom';

import { List, SquareMinus, SquarePlus } from 'lucide-react';
import { ButtonIcon, DefaultTable } from 'twyne-new.ui';

import { ReportEmpty } from '@/components/reports/body'
import optoutStore from 'modules/report/stores/optout'

const Details = ({ level }) => <>
  <ButtonIcon content='Details' className={`m-[${level ? 0 : -5}px]`} >
    <List size={16} />
  </ButtonIcon>
</>

const ReportsRevenueTable = ({ data, groupKeys, groupNames, updateParams }) => {
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = rowKey => {
    setExpandedRows(state => ({
      ...state,
      [rowKey]: !state[rowKey],
    }));
  };

  const handleExpandRow = row => {
    if (row.__level === 0 && row.__hasChildren) {
      toggleRow(row.__key);
    }
  };

  const getOptoutReportHref = (row, targetGroupKeys = groupKeys) => {
    let params = targetGroupKeys.reduce((nextParams, key) => {
      if (key !== 'siteId') {
        nextParams[key] = key === 'optoutdate' ? optoutStore.date(row[key]).format('YYYY-MM-DD') : row[key];
      }
      return nextParams;
    }, {});

    updateParams(params);
    params = new URLSearchParams(params);

    return `/reports/optouts?${params}`;
  };

  const actionFormat = level => (_, row) => {
    let linkProps = null;
    const key = groupKeys[level];
    if (level === 1 && (!row.siteId || String(optoutStore.siteId) === String(row.siteId))) {
      linkProps = { to: getOptoutReportHref(row) };
    } else if (level === 0 && (key !== 'siteId' || String(row.key) === String(optoutStore.siteId))) {
      linkProps = { to: getOptoutReportHref({ [key]: row.key }, [key]) };
    }

    if (linkProps) {
      return <Link {...linkProps} onClick={event => event.stopPropagation()}><Details level={level} /></Link>;
    }
    return <Details level={level} />;
  };

  const nameFormat = idx => value => {
    if (value === 'null') return 'N/A'
    switch (groupKeys[idx]) {
      case 'siteId':
        return optoutStore.getSiteName(value) || '--';
      case 'publisherId':
        return optoutStore.getPublisher(value).name || '--';
      case 'optoutdate':
        return optoutStore.date(value).format('MM/DD/YYYY');
      default:
        return value;
    }
  };

  const tableData = useMemo(() => (data || []).reduce((acc, row, index) => {
    const rowKey = `row-${index}`;
    const parent = {
      ...row,
      __key: rowKey,
      __level: 0,
      __hasChildren: Boolean(row.values && row.values.length),
    };
    acc.push(parent);

    if (expandedRows[rowKey]) {
      const children = (row.values || []).map((childRow, childIndex) => ({
        ...childRow,
        __key: `${rowKey}-child-${childIndex}`,
        __level: 1,
        __parentKey: rowKey,
      }));
      acc.push(...children);
    }

    return acc;
  }, []), [data, expandedRows]);

  if (!groupKeys.length) {
    return <div className='font-semibold p-6 text-center'>Select a group from filter.</div>
  }

  if (data) {
    return <ReportEmpty><div className='font-semibold p-6 text-center'>No results to show.</div></ReportEmpty>
  }

  const columns = [
    {
      dataField: 'name',
      text: groupNames.join(' / '),
      formatter: (_, row) => {
        if (row.__level === 0) {
          return <div className="flex items-center gap-2">
            {row.__hasChildren ? <span className="cursor-pointer" onClick={event => {
              event.stopPropagation();
              toggleRow(row.__key);
            }}>
              {expandedRows[row.__key] ? <SquareMinus size={16} /> : <SquarePlus size={16} />}
            </span> : <span style={{ width: '16px', display: 'inline-block' }} />}
            <span>{nameFormat(0)(row.key)}</span>
          </div>;
        }

        return <div style={{ paddingLeft: '24px' }}>{nameFormat(1)(row[groupKeys[1]])}</div>;
      },
    }, {
      dataField: 'count',
      text: 'Total',
      formatter: (cell, row) => {
        if (row.__level === 0) {
          return (row.values || []).reduce((prev, current) => prev + parseInt(current.count || 0), 0);
        }
        return cell;
      },
    }, {
      dataField: 'actions',
      text: '',
      className: "w-8",
      formatter: (_, row) => actionFormat(row.__level)(_, row),
    },
  ];

  const RowWrapper = ({ children, row }) => <tr onClick={() => handleExpandRow(row)}>{children}</tr>;

  return <DefaultTable
    keyField="__key"
    data={tableData}
    columns={columns}
    rowWrapper={RowWrapper}
  />;
};

export default ReportsRevenueTable;
